import { Reveal } from '../components/Reveal';
import { credentials, type Credential } from '../content/credentials';
import { useCountUp } from '../hooks/useCountUp';
import { useInView } from '../hooks/useInView';

function Cell({
  credential,
  start,
  delay,
}: {
  credential: Credential;
  start: boolean;
  delay: number;
}) {
  const counted = useCountUp(credential.value ?? 0, start);
  const isNumeric = credential.value !== undefined;

  return (
    <Reveal delay={delay} className="bg-white px-4 py-8 text-center sm:px-6">
      <div
        className={`font-display font-bold text-mint-700 ${
          isNumeric ? 'text-4xl sm:text-5xl' : 'text-xl leading-snug sm:text-2xl'
        }`}
      >
        {isNumeric ? `${counted}${credential.suffix ?? ''}` : credential.label}
      </div>
      <div className="mt-2 text-sm text-muted">{credential.caption}</div>
    </Reveal>
  );
}

export function CredentialsBand() {
  const { ref, inView } = useInView<HTMLDivElement>('0px 0px -80px 0px');

  return (
    <section aria-label="Credentials" className="relative z-10 -mt-14 px-5 sm:px-8">
      {/* gap-px over a tinted background gives hairline dividers without extra borders. */}
      <div
        ref={ref}
        className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-blob bg-mint-100 shadow-soft lg:grid-cols-4"
      >
        {credentials.map((credential, index) => (
          <Cell
            key={credential.caption + (credential.label ?? credential.value)}
            credential={credential}
            start={inView}
            delay={index * 90}
          />
        ))}
      </div>
    </section>
  );
}
